import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Play, Plus } from 'lucide-react';
import { API } from '../../App';

/**
 * Launcher card per design Q3 + Q7 corner cases.
 * Picks the parent fray for "New Version" using the locked Q7 rules.
 *   - empty: disabled, captioned
 *   - latest running/paused: fall back to latest completed
 *   - latest failed: fall back to latest completed
 *   - only failed (no completed): disabled, captioned
 */
function pickNewVersionParent(frays) {
  if (!frays || frays.length === 0) {
    return { fray: null, caption: 'No fray to iterate yet — start with a playbook or custom run.' };
  }
  const sorted = [...frays].sort(
    (a, b) => new Date(b.created_at) - new Date(a.created_at),
  );
  const latest = sorted[0];
  const completed = sorted.find((f) => f.status === 'completed');

  if (latest.status === 'completed') {
    return { fray: latest, caption: null };
  }
  if (completed) {
    if (latest.status === 'failed') {
      return {
        fray: completed,
        caption: `Iterating from ${completed.template_name} — ${latest.template_name} failed. Use 'Run custom' to retry.`,
      };
    }
    return {
      fray: completed,
      caption: `Iterating from ${completed.template_name} — ${latest.template_name} is still running.`,
    };
  }
  if (latest.status === 'failed') {
    return {
      fray: null,
      caption: `Latest fray failed — use 'Run custom' to retry, or open a specific fray to iterate from manually.`,
    };
  }
  return { fray: null, caption: `Wait for ${latest.template_name} to complete before iterating.` };
}

export default function LauncherCard({ projectId, frays = [] }) {
  const { fray: parent, caption } = pickNewVersionParent(frays);
  const enabled = !!parent;
  const navigate = useNavigate();

  const handleNewVersion = async () => {
    if (!enabled) return;
    try {
      // Auto-promote generated outputs from the parent fray to project scope
      // (#1101 Task 4.2). Per-iteration try/catch keeps a single failure from
      // skipping the rest of the queue — the project_contexts INSERT uses
      // ON CONFLICT so a re-run safely recovers any stuck mid-state.
      try {
        const ctxR = await API.get(`/api/frays/${parent.id}/contexts`);
        const direct = ctxR?.data?.direct || [];
        const generated = direct.filter((c) => c.source_fray_id === parent.id);
        for (const c of generated) {
          try {
            await API.post(`/api/projects/${projectId}/contexts`, {
              context_id: c.context_id,
              role: c.role || 'reference',
            });
            await API.delete(`/api/frays/${parent.id}/contexts/${c.context_id}`);
          } catch (oneErr) {
            // eslint-disable-next-line no-console
            console.error(`Auto-promote of ${c.context_id} failed (continuing):`, oneErr);
          }
        }
      } catch (promoteErr) {
        // eslint-disable-next-line no-console
        console.error('Auto-promote failed (proceeding with new-version):', promoteErr);
      }

      const r = await API.post(`/api/frays/${parent.id}/new-version`, { additional_context_ids: [] });
      const newId = r?.data?.id;
      if (newId) navigate(`/fray/${newId}/results`);
    } catch (e) {
      // TODO(#1101 Task 1.3): surface this error in the UI in a follow-up.
      // eslint-disable-next-line no-console
      console.error('New Version failed:', e);
    }
  };

  return (
    <div
      className="p-5 bg-[#1E1E1E] border border-[#2A2A2A] rounded-lg space-y-3"
      data-testid="launcher-card"
    >
      <button
        disabled={!enabled}
        data-testid="launcher-new-version"
        onClick={handleNewVersion}
        title={
          enabled
            ? `Auto-promotes ${parent.template_name}'s generated outputs to project scope, then iterates.`
            : undefined
        }
        aria-describedby={!enabled && caption ? 'launcher-caption' : undefined}
        className={
          'w-full flex items-center gap-2 px-4 py-2.5 font-mono text-xs uppercase tracking-wider rounded transition-colors ' +
          (enabled
            ? 'bg-[#FF4D00] hover:bg-[#FF6A2A] text-[#0A0A0A]'
            : 'bg-[#2A2A2A] text-[#A0A0A0] cursor-not-allowed')
        }
      >
        <Play className="w-3.5 h-3.5" />
        {parent ? `New Version of ${parent.template_name}` : 'New Version'}
      </button>

      {caption && (
        <p
          id="launcher-caption"
          className="text-[10px] font-mono text-[#A0A0A0] italic"
          data-testid="launcher-caption"
        >
          {caption}
        </p>
      )}

      <Link
        to={`/playbooks?project_id=${projectId}`}
        data-testid="launcher-run-playbook"
        className="w-full flex items-center gap-2 px-4 py-2 bg-[#0F0F0F] border border-[#2A2A2A] hover:border-[#FF4D00] text-xs font-mono text-[#F5F1E8] rounded"
      >
        <Plus className="w-3.5 h-3.5" />
        Run new playbook
      </Link>
      <Link
        to={`/fray/custom?project_id=${projectId}`}
        data-testid="launcher-run-custom"
        className="w-full flex items-center gap-2 px-4 py-2 bg-[#0F0F0F] border border-[#2A2A2A] hover:border-[#FF4D00] text-xs font-mono text-[#F5F1E8] rounded"
      >
        <Plus className="w-3.5 h-3.5" />
        Run custom
      </Link>
    </div>
  );
}

export { pickNewVersionParent };
